import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../../../auth.service';
import { UserService } from '../../../services/users/users.service';
import { ChatbotService } from '../../../services/chatbot/chatbot.service';
import { ContentLanguageService } from '../../../services/content-language.service';
import { ChatMessage } from '../../../models/chatbot.models';
import { MarkdownPipe } from '../../pipes/markdown.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, MarkdownPipe, TranslateModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss'
})
export class ChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput') messageInput?: ElementRef<HTMLTextAreaElement>;

  isOpen = false;
  chatbotEnabled = false;
  messages: ChatMessage[] = [];
  inputText = '';
  isLoading = false;
  errorMessage: string | null = null;

  private conversationId: number | null = null;
  private userMode = '';
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private chatbotService: ChatbotService,
    private userService: UserService,
    private cookieService: CookieService,
    private authService: AuthService,
    private contentLanguageService: ContentLanguageService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.resolveUserContext();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.isOpen = false;
  }

  /**
   * Click fuera del chatbot → cierra el panel. El host contiene tanto el panel
   * como el FAB, así que un click en cualquiera de los dos queda excluido (el
   * del FAB ya lo maneja `toggleChat`, y sin esto se cerraría al instante por
   * el bubbling). Escucha en `mousedown` para que el cierre no dependa de que
   * el elemento clickeado siga en el DOM al llegar el `click`.
   */
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.isOpen) return;

    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) return;

    this.isOpen = false;
  }

  get roleLabel(): string {
    switch (this.userMode) {
      case 'Student':  return 'Asistente del alumno';
      case 'Teacher':  return 'Asistente del tutor';
      default:         return 'Asistente del orientador';
    }
  }

  get userRoleLabel(): string {
    switch (this.userMode) {
      case 'Student':  return 'ALUMNO';
      case 'Teacher':  return 'TUTOR';
      default:         return 'ORIENTADOR';
    }
  }

  get inputPlaceholder(): string {
    switch (this.userMode) {
      case 'Student':  return 'Pregúntame sobre el programa...';
      case 'Teacher':  return 'Pregúntame sobre tus alumnos o clases...';
      default:         return 'Pregúntame por un alumno, grupo o alerta...';
    }
  }

  get showWelcome(): boolean {
    return this.messages.length === 0;
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.scrollToBottom();
      this.focusInput();
    }
  }

  sendMessage(): void {
    const text = this.inputText.trim();
    if (!text || this.isLoading) return;

    this.messages.push({ role: 'user', content: text, timestamp: new Date() });
    this.inputText = '';
    this.isLoading = true;
    this.errorMessage = null;
    this.scrollToBottom();

    this.chatbotService.sendMessage({
      message: text,
      conversation_id: this.conversationId,
      language_code: this.contentLanguageService.getContentLang()
    }).subscribe({
      next: (response) => {
        this.conversationId = response.conversation_id;
        this.messages.push({ role: 'assistant', content: response.answer, timestamp: new Date() });
        this.isLoading = false;
        this.scrollToBottom();
        this.focusInput();
      },
      error: (error) => {
        this.isLoading = false;
        this.focusInput();
        if (error.status === 403) {
          this.chatbotEnabled = false;
          this.isOpen = false;
          return;
        }
        if (error.status === 401) {
          // El interceptor global ya redirige a /signin y borra las cookies
          return;
        }
        if (error.status === 404) {
          this.conversationId = null;
        }
        this.errorMessage = this.getErrorMessage(error.status);
        this.scrollToBottom();
      }
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  dismissError(): void {
    this.errorMessage = null;
  }

  startNewConversation(): void {
    this.conversationId = null;
    this.messages = [];
    this.errorMessage = null;
    this.focusInput();
  }

  private resolveUserContext(): void {
    const currentUser = this.userService.getCurrentUserValue();
    if (currentUser) {
      this.applyUserContext(currentUser);
      return;
    }
    const token = this.cookieService.get('gr_access');
    if (!token) return;
    const decoded = this.authService.getDecodedAccessToken(token);
    if (!decoded?.user_id) return;
    this.userService.getUserProfileData(decoded.user_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({ next: (user) => this.applyUserContext(user) });
  }

  private applyUserContext(user: any): void {
    this.userMode = user?.user_type ?? '';
    this.chatbotEnabled = user?.users_organizations?.[0]?.chatbot_enabled ?? false;
  }

  private getErrorMessage(status: number): string {
    switch (status) {
      case 400: return 'No se pudo procesar el mensaje. Intentá de nuevo.';
      case 401: return 'Tu sesión expiró. Por favor, volvé a iniciar sesión.';
      case 404: return 'No se encontró la conversación. Podés seguir escribiendo para iniciar una nueva.';
      case 502: return 'El asistente no está disponible en este momento. Intentá en unos minutos.';
      default:  return 'Ocurrió un error inesperado. Intentá de nuevo.';
    }
  }

  /**
   * Devuelve el foco al input del chat. Hace falta porque el textarea se
   * deshabilita mientras `isLoading` (el navegador lo blurea al pasar a
   * disabled) y al re-habilitarlo el foco no vuelve solo: sin esto había que
   * clickear de nuevo para seguir escribiendo. El `setTimeout` espera a que
   * Angular re-habilite el elemento — un `focus()` sobre un textarea disabled
   * no hace nada.
   */
  private focusInput(): void {
    setTimeout(() => {
      if (!this.isOpen) return;
      const input = this.messageInput?.nativeElement;
      if (input && !input.disabled) input.focus();
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        this.messagesContainer.nativeElement.scrollTop =
          this.messagesContainer.nativeElement.scrollHeight;
      }
    }, 50);
  }
}
